import React, {Component} from 'react';
import { Link, Redirect } from 'react-router-dom';
import mbxClient from '@mapbox/mapbox-sdk';
import mbxGeocoding from '@mapbox/mapbox-sdk/services/geocoding';
import ReCAPTCHA from "react-google-recaptcha";
import Pica from 'pica';
import classnames from 'classnames';
import { debounce } from 'lodash';
import isMobile from 'ismobilejs';
import Autosuggest from 'react-autosuggest';

import APIClient from './Api';
import tag from './Analytics';
import { createOrUpdatePositionMarker } from './MapUtils';
import { TESTIMONIAL_CHOICES } from './TestimonialWizard';


const api = new APIClient();
const baseClient = mbxClient({ accessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN });
const geocodingClient = mbxGeocoding(baseClient);
const pica = Pica();


class Form extends Component {

  constructor(props) {
    super(props);
    this.recaptcha = React.createRef();
    this.state = {
      firstname: '', firstnameValid: true,  // Fields are initially valid to avoid having everything in red at start
      lastname: '', lastnameValid: true,
      hidename: false,
      age: '', ageValid: true,
      city: '', cityValid: true,
      shortCodes: [],
      citySuggestions: [],
      message: '', messageValid: true,
      email: '', emailValid: true,
      optin: false,
      optin_contact: false,
      optin_contact_media: false,
      pos: null, posValid: true, cityLabelForPos: null,  // Store last city label corresponding to pos
      captchaValid: true,
      sendTried: false,
      sendOK: false,
      image: null,
      imageFileName: '',
      cancelled: false
     };
    this.mandatory = ['firstname', 'lastname', 'age', 'email', 'city', 'message', 'pos'];
    this.imageBuffer = new Image();
    this.imageBuffer.addEventListener('load', this.handleImageLoaded, false);
    this.debouncedLoadSuggestions = debounce(this.loadSuggestions, 200);
    tag('form');

  }

  // Form and fields stuff

  testField(name, value) {
    if (this.mandatory.includes(name)) {
      if (name === 'city') {
        // City is valid only if position is valid and user has not changed the input since
        return value != null && value !== '' &&
          this.validateFieldWithCurrentValue('pos') && this.state.cityLabelForPos === value;
      }
      return value != null && value !== '';
    }
    return true;
  }

  validateFieldWithCurrentValue(name) {
    if (name == null) {
      throw Error('name cannot be null');
    }
    let valid = this.testField(name, this.state[name]);
    this.setState({[name + 'Valid']: valid});
    return valid;
  }

  validateFieldsWithCurrentValues() {
    var res =  this.mandatory.map(
      (name, index) => {
        return this.validateFieldWithCurrentValue(name);
      });
    // Note: don't test states, they have not been updated yet
    return res.every((x) => x);
  }

  handleChange = (event) => {
    // Validate current changed or blurred field, so that the user gradually knows what is required
    const target = event.target;
    if (target != null) {
      const value = target.type === 'checkbox' ? target.checked : target.value;
      const name = target.name;

      this.setState({
        [name]: value,
        [name + 'Valid']: this.testField(name, value),
        sendTried: false
      });
    }
  }


  loadSuggestions = (value) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;
    const suggestions = [];
    if (inputLength !== 0) {
      geocodingClient.forwardGeocode({
        query: value,
        types: ['postcode', 'place'],   // ~= cities, cf. https://docs.mapbox.com/api/search/#data-types
        countries: ['FR', 'BL', 'MF', 'MQ', 'GP', 'RE', 'YT', 'NC', 'PF', 'WF', 'PM', 'TF', 'GF'],
        language: ['fr']
      })
      .send()
      .then(response => {
        const match = response.body;
        if (response && response.body && response.body.features && response.body.features.length) {
          console.log(response.body.features);
          this.setState({
            citySuggestions: response.body.features
          });
        }
      })
    }
  }

  onSuggestionsFetchRequested = ({ value }) => {
    this.debouncedLoadSuggestions(value);
  };

  onSuggestionSelected = (event, {suggestion}) => {
    const { testimonialChoices } = this.props;
    const { testimonialChoice , majorChoice } = testimonialChoices;
    const icon = (
      TESTIMONIAL_CHOICES[testimonialChoice].major_choices[majorChoice].icon ||
      undefined
    );

    this.setState({
      pos: createOrUpdatePositionMarker(this.state.pos, suggestion.center, suggestion.text, icon),
      posValid: true,
      city: suggestion.text,
      cityValid: true,
      cityLabelForPos: suggestion.text,
      shortCodes: suggestion.context.map(s => s.short_code).filter(c => c !== undefined),
    });
  }

  handleImageChange = (event) => {
    const target = event.target;
    var files = target.files;

    if (files.length === 0) { return; }

    this.imageBuffer.src = window.URL.createObjectURL(files[0]);
    this.setState({imageFileName: files[0].name});
  }

  handleImageLoaded = (event) => {
    /*
    console.log(this.imageBuffer.width);
    console.log(this.imageBuffer.height);
    */

    var offScreenCanvas = document.createElement('canvas')
    offScreenCanvas.width  = 1024;
    offScreenCanvas.height = this.imageBuffer.height * 1024.0 / this.imageBuffer.width;

    pica.resize(this.imageBuffer, offScreenCanvas)
      .then(result => pica.toBlob(result, 'image/jpeg', 0.90))
      .then(blob => {
        this.setState({image: blob});
        console.log('resized to canvas & created blob!')
      });
  }

  createContrib = (image='') => {
    var data = {
      firstname: this.state.firstname,
      lastname: this.state.lastname,
      hidename: this.state.hidename,
      age: this.state.age,
      city: this.state.city,
      iso_code: this.state.shortCodes.length ? this.state.shortCodes[0] : '',
      message: this.state.message,
      email: this.state.email,
      lng: this.state.pos.getLngLat().lng,
      lat: this.state.pos.getLngLat().lat,
      optin: this.state.optin,
      optin_contact: this.state.optin_contact,
      optin_contact_media: this.state.optin_contact_media,
      captcha: this.state.captcha,
      image: image,
      utm_source: this.props.qs ? this.props.qs.utm_source : '',
      utm_medium: this.props.qs ? this.props.qs.utm_medium : '',
      utm_campaign: this.props.qs ? this.props.qs.utm_campaign : '',
      utm_content: this.props.qs ? this.props.qs.utm_content : '',
      utm_term: this.props.qs ? this.props.qs.utm_term : '',
      testimonial_choice: this.props.testimonialChoices.testimonialChoice,
      major_reason_choice: this.props.testimonialChoices.majorChoice,
      minor_reason_choices: this.props.testimonialChoices.minorChoices
    };
    console.log(data);
    api.createContrib(data).then( (data) =>
      {
        this.setState({
          sendTried: true,
          sendOK: true
        }, () => this.props.onAddSuccess(data));
        // console.log(data);
      }).catch( (err) =>  {
        this.setState({
          sendTried: true,
          sendOK: false
        });
      }
    );
  }

  handleSubmit = (event) => {
    // validate form and execute invisible captcha
    // Effective submit will be triggered on captcha change
    event.preventDefault();
    if (this.validateFieldsWithCurrentValues()) {
      this.recaptcha.current.execute();
    }
  }

  handleCaptchaChange = (value) => {
    // trigger post submission
    this.setState({
      captcha: value,
      captchaValid: this.testField('captcha', value)
    }, () => this.submitForm());
  }

  submitForm = () => {
    this.setState({ sendTried: false, sendOK: false });
    console.log(this.state);
    if (this.validateFieldsWithCurrentValues()) {  // Force validation
      if (this.state.image) {
        var blobReader = new FileReader();
        blobReader.addEventListener("loadend", () => {
          this.createContrib(blobReader.result);
        });
        blobReader.readAsDataURL(this.state.image);
      } else {
        this.createContrib();
      }
    } else {
      console.log('Fields not validated!');
    }
  }

  handleCancel = (event) => {
    event.preventDefault();
    const { pos } = this.state;
    if (pos) {
      pos.remove();
    }
    this.setState({pos: null, cancelled: true});
  }

  isValid (name) {
    return this.state[name + 'Valid'];
  }

  isAllValid () {
    return this.mandatory.every((x) => { return this.isValid(x) });
  }

  fieldClass (name, otherClassName) {
    return this.isValid(name) ? otherClassName : otherClassName + " is-danger";
  }

  fieldIcon (name) {
    return this.isValid(name) ? "" :
      (<span className="icon is-small is-right">
        <i className="fas fa-exclamation-triangle"></i>
      </span>);
  }

  // Render stuff

  render() {
    if (this.state.cancelled) {
      return (<Redirect to ="/"/>);
    }
    if (this.state.sendTried && this.state.sendOK ){
      return (<Redirect to="/success"/>)
    }
    return (
      <div className="gp-popup-choix">

        <div class="gp-close-icon" onClick={this.props.onClose}>
          <span class="icon">
            <i class="fa fa-window-close"></i>
          </span>
        </div>

        <form className="gp-content1 gp-no-counter" onSubmit={this.handleSubmit}>

          <div className="gp-titre-s4">
            Je donne plus de détails
          </div>

          <div className="gp-form-explinations">
            Les champs marqués par <span className="gp-mandatory"/> sont obligatoires
          </div>

          <div className="field">
            <label className="label">
              Votre témoignage<span className="gp-mandatory"/>
            </label>
            <div className="control has-icons-right">
              <textarea
                onChange={this.handleChange}
                onBlur={this.handleChange}
                value={this.state.message}
                name="message"
                maxLength="2000"
                className={this.fieldClass('message', 'textarea')}
                placeHolder="2000 caractères maximum"
              ></textarea>
              { this.fieldIcon('message') }
            </div>
          </div>


          <div className="field gp-file">
            <label className="label">J'ajoute une photo à mon témoignage</label>
            <div class={classnames("file", this.state.imageFileName !== '' ? "has-name" : "")}>
              <label class="file-label">
                <input class="file-input" type="file" name="image" onChange={this.handleImageChange}/>
                <span class="file-cta">
                  <span class="file-icon">
                    <i class="fas fa-upload"></i>
                  </span>
                  <span class="file-label">
                    Choisir un fichier...
                  </span>
                </span>
                { this.state.imageFileName !== '' ?
                  <span class="file-name">
                    {this.state.imageFileName}
                  </span>
                  : ''
                }
              </label>
            </div>
          </div>

          <div className="field required">
            <label className="label">
              Prénom<span className="gp-mandatory"/>
            </label>
            <div className="control has-icons-right">
              <input
                type="text"
                onChange={this.handleChange}
                onBlur={this.handleChange}
                value={this.state.firstname}
                name="firstname"
                maxLength="80"
                className={this.fieldClass('firstname', 'input')}
              />
              { this.fieldIcon('firstname') }
            </div>
          </div>

          <div className="field required">
            <label className="label">
              Nom<span className="gp-mandatory"/>
            </label>
            <div className="control has-icons-right">
              <input
                type="text"
                onChange={this.handleChange}
                onBlur={this.handleChange}
                value={this.state.lastname}
                name="lastname"
                maxLength="80"
                className={this.fieldClass('lastname', 'input')}
              />
              { this.fieldIcon('lastname') }
            </div>
          </div>

          <div className="field">
            <div className="control">
              <label className="checkbox">
                <input
                  type="checkbox"
                  onChange={this.handleChange}
                  onBlur={this.handleChange}
                  value={this.state.hidename}
                  name="hidename"/>
                Je ne souhaite pas que mon nom s'affiche
              </label>
            </div>
          </div>

          <div className="field required">
            <label className="label">
              E-mail<span className="gp-mandatory"/>
            </label>
            <div className="control has-icons-left has-icons-right">
              <input
                type="email"
                onChange={this.handleChange}
                onBlur={this.handleChange}
                value={this.state.email}
                name="email"
                maxLength="120"
                className={this.fieldClass('email', 'input')}
                placeholder="Entrez votre e-mail"
              />
              <span className="icon is-small is-left">
                <i className="fas fa-envelope"></i>
              </span>
              { this.fieldIcon('email') }
            </div>
          </div>

          <div className="field required">
            <label className="label">
              Âge<span className="gp-mandatory"/>
            </label>
            <div className="control">
              <input
                type="number"
                onChange={this.handleChange}
                onBlur={this.handleChange}
                value={this.state.age}
                name="age"
                min="0"
                step="1"
                className={this.fieldClass('age', 'input')}
              />
            </div>
          </div>

          <div className="field required">
            <label className="label">
              Commune<span className="gp-mandatory"/>
            </label>
            <div className="control has-icons-right">
              <Autosuggest
                suggestions={this.state.citySuggestions}
                onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                // onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                getSuggestionValue={suggestion => suggestion.text}
                renderSuggestion={suggestion => (
                  <div className="gp-city-suggest-option">
                    {suggestion.text}
                  </div>
                )}
                shouldRenderSuggestions={value => value.trim().length > 1}
                highlightFirstSuggestion={true}
                focusInputOnSuggestionClick={!isMobile().any}
                onSuggestionSelected={this.onSuggestionSelected}
                theme={{suggestionHighlighted: "gp-city-suggest-option-highlighted"}}
                inputProps={
                  {
                    maxLength: 80,
                    value: this.state.city,
                    onChange: this.handleChange,
                    onBlur: this.handleChange,
                    type: 'search',
                    name: 'city',
                    className: this.fieldClass('city', 'input')
                  }
                }
              />
              { this.fieldIcon('city') }
            </div>
            {!this.isValid('city') && this.state.citySuggestions.length > 0 ?(
              <p className="help is-danger">Veuillez choisir une ville parmi les suggestions</p>
            ) : ""}
          </div>

          <div className="field">
            <div className="control">
              <label className="checkbox">
                <input
                  type="checkbox"
                  onChange={this.handleChange}
                  onBlur={this.handleChange}
                  value={this.state.optin}
                  name="optin"
                />
                Je souhaite recevoir des informations de la part de l'Affaire du Siècle.
              </label>
            </div>
          </div>

          <div className="field">
            <div className="control">
              <label className="checkbox">
                <input
                  type="checkbox"
                  onChange={this.handleChange}
                  onBlur={this.handleChange}
                  value={this.state.optin_contact}
                  name="optin_contact"
                />
                J'accepte d'être recontacté·e pour que mon témoignage puisse
                être ajouté au dossier juridique.
              </label>
            </div>
          </div>

          <div className="field">
            <div className="control">
              <label className="checkbox">
                <input
                  type="checkbox"
                  onChange={this.handleChange}
                  onBlur={this.handleChange}
                  value={this.state.optin_contact_media}
                  name="optin_contact_media"
                />
                J'accepte d'être recontacté⋅e pour témoigner dans les médias.
              </label>
            </div>
          </div>

          <div class="field">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={this.handleCaptchaChange}
              ref={this.recaptcha}
              theme="light"
              hl="fr"
              className={this.fieldClass('captcha', 'gp-captcha')}
              size="invisible"
            />
          </div>

          <div className="field">
            { this.isAllValid() ? "" : (<p className="help is-danger">Merci de compléter les champs obligatoires</p>) }
          </div>

          <div className="field">
            <div class="gp-action-form-large">
              <div class="gp-send-send-column is-right">
                <div className="control">
                  <button
                    type="submit"
                    className={
                      classnames(
                        "button is-link gp-button-form-submit",
                        this.state.sendTried && !this.state.sendOK ? "is-danger " : ""
                      )
                    }>
                    Valider
                  </button>
                </div>
              </div>
              <div class="gp-send-cancel-column is-left">
                <div className="control">
                  <button
                    className="button is-link gp-button-form-cancel"
                    onClick={this.handleCancel}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="field">
            { this.state.sendTried && !this.state.sendOK ? (
              <p className="help is-danger">Une erreur s'est produite lors de l'envoi de votre message, veuillez réessayer</p>
            ) : "" }
          </div>

        </form>
      </div>
    );
  }
}

export default Form;
